import {IBoardObjectNode} from "../types/IBoardObjectNode";
import {INode} from "../types/INode";
import {IBoardObject} from "../types/IBoardObject";
import {IBoardWidget} from "../types/IBoardWidget";
import {IBoardObjectDefinition} from "../types/IBoardObjectDefinition";
import {IBoardObjectDefinitionNode} from "../types/IBoardObjectDefinitionNode";
import { findNodeById } from '../../../common/utility/hierarchyHelper';

const useBoardContentObject = () => {
  const _createUpdateWidgetNode = (boardObject: IBoardObject, widget: any, objectNode: IBoardObjectNode, isGroup: boolean) => {
    let widgetNode = widget.nodes.find((item) => item.objectPath === boardObject.objectPath && item.nodeId === objectNode.id);
    if (!widgetNode) {
      widgetNode = {
        objectPath: boardObject.objectPath,
        nodeId: objectNode.id || 0,
        name: objectNode.name,
        fullName: objectNode.fullName,
        isGroup: isGroup,
        exclude: false
      };
      widget.nodes.push(widgetNode);
    } else {
      widgetNode.name = objectNode.name;
      widgetNode.fullName = objectNode.fullName;
    }
    return widgetNode;
  }

  const _loadWidgetNodes = (boardObject: IBoardObject, widget: any) => {
    const lookup = new Set();
    let isGroup = false;
    let objectNodes : IBoardObjectNode[];

    if (widget.setup.sliceGroup) {
      isGroup = true;
      const groupId = parseInt(widget.setup.sliceGroup);
      objectNodes = !isNaN(groupId) ? boardObject.nodes.filter((item) => item.objectId === groupId) : [];
    } else
      objectNodes = boardObject.nodes.filter((item) => !item.isDescendent);

    objectNodes.forEach((objectNode) => {
      _createUpdateWidgetNode(boardObject, widget, objectNode, isGroup);
      lookup.add(objectNode.id);
    });

    for (let i = widget.nodes.length - 1; i > -1; i--) {
      const widgetNode = widget.nodes[i];
      if (widgetNode.objectPath === boardObject.objectPath) {
        if (!lookup.has(widgetNode.nodeId))
          widget.nodes.splice(i, 1);
      }
    }
  }

  const loadObjectWidgetNodes = (object : IBoardObject, widgets : IBoardWidget[]) => {
    widgets.forEach((widget) => {
      if (widget.type !== 'kpiChart' && widget.type !== 'kpiGrid') {
        _loadWidgetNodes(object, widget);
      }
    })
  }

  const _objectPath = (objectId : number|string, parentObject : IBoardObject|null, type : string) => {
    return `${parentObject ? `${parentObject.objectPath}-` : ''}${objectId}${type === '_dim' ? type : ''}`;
  }

  const generateObjects = (boardObjects : IBoardObject[], objectDefinitions : IBoardObjectDefinition[], workspaceNodes : INode[]) => {
    const pathHash = {};

    objectDefinitions.forEach((definition) => {
      if (!definition.parentDefinitionPath)
        _buildObjects(boardObjects, objectDefinitions, workspaceNodes, definition, null, pathHash);
    });

    for (let j = boardObjects.length - 1; j > -1; j--) {
      let boardObject = boardObjects[j];
      let objectNodes = pathHash[boardObject.objectPath];
      if (!objectNodes)
        boardObjects.splice(j, 1);
      else {
        _removeNonExistingNodes(boardObject, objectNodes);
      }
    }

    return boardObjects;
  }

  const _removeNonExistingNodes = (boardObject, nodeHierarchy) => {
    for (let i = boardObject.nodes.length - 1; i > -1; i--) {
      const node = boardObject.nodes[i];
      if (!findNodeById(nodeHierarchy, node.id, 'id', 'items'))
        boardObject.nodes.splice(i, 1);
    }
  }

  const _buildObjects = (boardObjects : IBoardObject[],
                         objectDefinitions : IBoardObjectDefinition[],
                         workspaceNodes : INode[],
                         objectDefinition : IBoardObjectDefinition,
                         parentObject : IBoardObject|null,
                         pathHash : any) => {

    let nodesToBuild = objectDefinition.nodes.filter((item) => !item.parentNodeId);

    if (objectDefinition.isGroup && !objectDefinition.showGroup) {
      const firstLevel : any[] = [];

      nodesToBuild.forEach(function (rootNode) {
        const append = objectDefinition.nodes.filter((item) => item.parentNodeId === rootNode.nodeId && !item.exclude);
        append.forEach(function (node) {
          firstLevel.push(node);
        });
      });

      nodesToBuild = firstLevel;
    }

    nodesToBuild.forEach((nodeDefinition) => {
      _buildObjectFromNodeDefinition(boardObjects, workspaceNodes, nodeDefinition, objectDefinition, parentObject, pathHash);
    });

    if (!objectDefinition.isGroup) {
      const parent = boardObjects.find((item) => {
        return item.definitionPath === objectDefinition.definitionPath &&
          item.type === objectDefinition.type;
      }) || null;

      const childDefinitions = objectDefinitions.filter((item) => item.parentDefinitionPath === objectDefinition.definitionPath);
      childDefinitions.forEach((child) => {
        _buildObjects(boardObjects, objectDefinitions, workspaceNodes, child, parent, pathHash);
      });
    }
  }

  const _addNodeAndDescendents = (boardObject : IBoardObject, node : INode, isDescendent : boolean) => {
    _addNode(boardObject, node, isDescendent);
    if (node.items) {
      for (let i = 0; i < node.items.length; i++) {
        _addNodeAndDescendents(boardObject, node.items[i], true);
      }
    }
  }

  const _addNode = (boardObject: IBoardObject, node: INode, isDescendent: boolean) => {
    let objectNode = _findObjectNode(boardObject, node);

    if (!objectNode) {
      objectNode = {
        id: node.id,
        //exclude: false,
        isDescendent: isDescendent,
        objectId: node.objectId,
        name: node.name,
        fullName: node.fullName
        //childNodes: []
      };

      boardObject.nodes.push(objectNode);
    }
    return objectNode;
  }

  const _findObjectNode = (boardObject : IBoardObject, nodeId) => {
    let id = parseInt(nodeId);
    return !isNaN(id) ? boardObject.nodes.find((item) => item.id === id) : null;
  }

  const _buildObjectFromNodeDefinition = (boardObjects : IBoardObject[],
                                          workspaceNodes : INode[],
                                          nodeDefinition : IBoardObjectDefinitionNode,
                                          objectDefinition : IBoardObjectDefinition,
                                          parentObject : IBoardObject|null,
                                          pathHash : any) => {
    const node : INode = findNodeById(workspaceNodes, nodeDefinition.nodeId, 'id', 'items');
    if (node) {
      let type = node.type === '_dim' ? '_dim' : 'obj';
      let path = _objectPath(nodeDefinition.objectId || 0, parentObject, type);

      let lNodes = pathHash[path];
      if (!lNodes) {
        lNodes = [];
        pathHash[path] = lNodes;
      }
      lNodes.push(node);

      let boardObject = boardObjects.find((item) => item.objectPath === path); //this.findObjectByPath(path);
      if (!boardObject) {
        boardObject = {
          objectId: nodeDefinition.objectId,
          type: type,
          name: nodeDefinition.name,
          objectPath: path,
          //isGroup: objectDefinition.isGroup && !nodeDefinition.parentNodeId,
          parentObjectPath: parentObject ? parentObject.objectPath : null,
          definitionPath: objectDefinition.definitionPath,
          nodes: [],
          nodesOfObject: []
          //nodeHierarchy: []
        };
        boardObjects.push(boardObject);
      }
      _addNodeAndDescendents(boardObject, node, false);

      const childNodes = objectDefinition.nodes.filter((item) => item.parentNodeId === nodeDefinition.nodeId && !item.exclude);
      for (let i = 0; i < childNodes.length; i++)
        _buildObjectFromNodeDefinition(boardObjects, workspaceNodes, childNodes[i], objectDefinition, boardObject, pathHash);
    }
  }

  const nodesFor = (nodes: IBoardObjectNode[], objectId: number): IBoardObjectNode[] | null => {
    if (!isNaN(objectId))
      return nodes.filter((n) => n.objectId === objectId)
    else return null;
  }

  const findNode = (nodes: IBoardObjectNode[], nodeId: number): IBoardObjectNode | null => {
    if (!isNaN(nodeId))
      return nodes.find((item) => item.id === nodeId) || null;
    else
      return null;
  }

  const childNodes = (nodes: IBoardObjectNode[], nodeId: number): IBoardObjectNode[] | null => {
    let node = findNode(nodes, nodeId);

    if (!node) return [];

    if (node.childNodes) {
      return node.childNodes.filter((item) => !item.exclude);
    } else {
      return []
    }
  };

  const _loadChildNodesByPassGroups = (loadInto: IBoardObjectNode[], node: IBoardObjectNode) => {
    if(node.childNodes){
      node.childNodes.forEach((child) => {
        if (child.isGroup) {
          _loadChildNodesByPassGroups(loadInto, child);
        } else {
          if (!child.exclude) loadInto.push(child);
        }
      });
    }
  };

  const childNodesByPassGroups = (nodes: IBoardObjectNode[], nodeId: number): IBoardObjectNode[] => {
    let node = findNode(nodes, nodeId);
    if (!node) return [];

    let childNodes = [];
    _loadChildNodesByPassGroups(childNodes, node);
    return childNodes;
  };

  const nodesForCategoryTypeLevel = (nodes: IBoardObjectNode[], categoryTypeId: number, categoryLevel: number):  IBoardObjectNode[] => {
    let categoryNodes: any[] = [];

    nodes.forEach((node) => {
      !node.exclude && node.categoryTypeId === categoryTypeId && node.categoryLevel === categoryLevel && categoryNodes.push(node);
    });

    return categoryNodes;
  };

  const nodesForType = (nodes: IBoardObjectNode[], type: string): IBoardObjectNode[] => {
    return nodes.filter(function (item) {
      return item.type === type && !item.exclude;
    });
  };

  const buildNodeHierarchy = (nodes: IBoardObjectNode[]): IBoardObjectNode[] => {
    let nodeHierarchy: IBoardObjectNode[] = [];
    for (let i = 0; i < nodes.length; i++) {
      let node = nodes[i];
      node.childNodes = node.childNodes || [];

      if (!node.parentId) {
        nodeHierarchy.push(node);
        continue;
      }

      let parent: IBoardObjectNode | undefined = nodes.find((item) => {
        return item.id === node.parentId;
      });

      if (parent) {
        parent.childNodes = parent.childNodes || [];
        parent.childNodes.push(node);
        node.parentNode = parent;
      } else nodeHierarchy.push(node);
    }
    return nodeHierarchy;
  };

  const payload = (objects: IBoardObject[]) => {
    return objects.map((obj) => {
      return {
        objectId: obj.objectId,
        type: obj.type,
        parentId: obj.parentId,
        objectPath: obj.objectPath,
        parentObjectPath: obj.parentObjectPath,
        definitionPath: obj.definitionPath,
        nodes: obj.nodes.map((node) => {
          return {
            id: node.id,
            isDescendent: node.isDescendent,
            exclude: node.exclude
          };
        })
      };
    });
  };

  return {
    nodesFor,
    findNode,
    nodesForType,
    buildNodeHierarchy,
    nodesForCategoryTypeLevel,
    childNodesByPassGroups,
    childNodes,
    loadObjectWidgetNodes,
    generateObjects,
    payload
  }
}

export default useBoardContentObject;